/**
 * SetupInstructionsModal - Live audio input setup guide
 */
import React, { memo } from 'react';
import { Button } from './ui/button';

const SetupInstructionsModal = memo(({ isVisible, onClose }) => {
  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="setup-instructions-modal">
      <div className="bg-gradient-to-br from-purple-900 to-gray-900 p-6 rounded-xl max-w-2xl w-full mx-4 border border-purple-500/30 max-h-[90vh] overflow-y-auto">
        <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
          Live Audio Input Setup
        </h3>

        <div className="space-y-6">
          {/* Introduction */}
          <div className="bg-purple-500/10 border border-purple-400/20 p-4 rounded-lg">
            <div className="text-purple-200 text-sm">
              To capture system audio (from your DAW, Spotify, YouTube, etc.), you need to install a free virtual audio loopback device. This creates a &ldquo;virtual cable&rdquo; that routes audio from your applications into NeuroWave One.
            </div>
          </div>

          {/* macOS - BlackHole */}
          <div className="space-y-3">
            <h4 className="text-lg font-bold text-white flex items-center gap-2">
              macOS - BlackHole (Free)
            </h4>
            <div className="bg-black/30 p-4 rounded-lg space-y-2">
              <ol className="list-decimal list-inside space-y-2 text-white/80 text-sm">
                <li>Download BlackHole from: <code className="bg-purple-500/20 px-2 py-1 rounded text-purple-300">https://existential.audio/blackhole/</code></li>
                <li>Install BlackHole 2ch (2-channel version)</li>
                <li>Open <strong>Audio MIDI Setup</strong> (in Applications &rarr; Utilities)</li>
                <li>Click the <strong>+</strong> button and select <strong>&ldquo;Create Multi-Output Device&rdquo;</strong></li>
                <li>Check both <strong>&ldquo;BlackHole 2ch&rdquo;</strong> and your <strong>headphones/speakers</strong></li>
                <li>Set this Multi-Output Device as your system output</li>
                <li>In your DAW or audio app, route output to this Multi-Output Device</li>
                <li>In NeuroWave One, click <strong>&ldquo;Enable Live Input&rdquo;</strong> and select <strong>&ldquo;BlackHole 2ch&rdquo;</strong> when prompted</li>
              </ol>
            </div>
          </div>

          {/* Windows - VB-Cable */}
          <div className="space-y-3">
            <h4 className="text-lg font-bold text-white flex items-center gap-2">
              Windows - VB-Cable (Free)
            </h4>
            <div className="bg-black/30 p-4 rounded-lg space-y-2">
              <ol className="list-decimal list-inside space-y-2 text-white/80 text-sm">
                <li>Download VB-Cable from: <code className="bg-purple-500/20 px-2 py-1 rounded text-purple-300">https://vb-audio.com/Cable/</code></li>
                <li>Install VB-Cable (run as administrator)</li>
                <li>Restart your computer after installation</li>
                <li>Right-click the <strong>speaker icon</strong> in taskbar &rarr; <strong>Sound settings</strong></li>
                <li>Set output device to <strong>&ldquo;CABLE Input (VB-Audio Virtual Cable)&rdquo;</strong></li>
                <li>Open browser and allow microphone access when prompted</li>
                <li>Select <strong>&ldquo;CABLE Output (VB-Audio Virtual Cable)&rdquo;</strong> as input</li>
                <li>Click <strong>&ldquo;Enable Live Input&rdquo;</strong> in NeuroWave One</li>
              </ol>
              <div className="bg-yellow-500/20 border border-yellow-400/30 p-3 rounded mt-3">
                <div className="text-yellow-200 text-xs">
                  <strong>Note:</strong> You won&rsquo;t hear audio through speakers while VB-Cable is set as output. To monitor, set up &ldquo;Listen to this device&rdquo; in Sound settings, or use VB-Audio&rsquo;s Voicemeeter for more advanced routing.
                </div>
              </div>
            </div>
          </div>

          {/* Linux - PulseAudio */}
          <div className="space-y-3">
            <h4 className="text-lg font-bold text-white flex items-center gap-2">
              Linux - PulseAudio Loopback
            </h4>
            <div className="bg-black/30 p-4 rounded-lg space-y-2">
              <div className="text-white/80 text-sm space-y-2">
                <p>PulseAudio has built-in loopback capability:</p>
                <code className="block bg-black/50 p-3 rounded text-green-300 text-xs">
                  pactl load-module module-loopback latency_msec=1
                </code>
                <p>To make it permanent, add this to <code className="bg-purple-500/20 px-1 rounded">/etc/pulse/default.pa</code>:</p>
                <code className="block bg-black/50 p-3 rounded text-green-300 text-xs">
                  load-module module-loopback latency_msec=1
                </code>
                <p>Then restart PulseAudio:</p>
                <code className="block bg-black/50 p-3 rounded text-green-300 text-xs">
                  pulseaudio -k && pulseaudio --start
                </code>
              </div>
            </div>
          </div>

          {/* Tips */}
          <div className="bg-blue-500/10 border border-blue-400/20 p-4 rounded-lg">
            <h4 className="text-white font-bold mb-2 flex items-center gap-2">
              Tips
            </h4>
            <ul className="list-disc list-inside space-y-1 text-white/70 text-sm">
              <li>The audio level meter shows if audio is being captured correctly</li>
              <li>Live audio is monitoring-only (no playback to prevent feedback)</li>
              <li>8D effects are applied during recording, not live playback</li>
              <li>Audio already plays from your source app - no need to play back</li>
            </ul>
          </div>

          <Button
            onClick={onClose}
            className="w-full bg-purple-600 hover:bg-purple-700"
            data-testid="setup-instructions-close-btn"
          >
            Got It!
          </Button>
        </div>
      </div>
    </div>
  );
});

SetupInstructionsModal.displayName = 'SetupInstructionsModal';

export default SetupInstructionsModal;
